import React, { useState, ReactElement, PropsWithChildren, useEffect, useCallback } from "react";
import Auth from "@aws-amplify/auth";
import { AuthConfig } from "../../lib/AuthConfig";
import { getLocalStorage, getURL, redirect } from "../../backend/dom";
import { KatSpinner } from "@amzn/katal-react";

const AUTH_STATUS = {
  AUTHENTICATED: "AUTHENTICATED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
} as const;

type AuthStatusType = typeof AUTH_STATUS[keyof typeof AUTH_STATUS];

export const AuthWrapper: React.FC<PropsWithChildren<Record<string, unknown>>> = ({ children }): ReactElement => {
  const [authStatus, setAuthStatus] = useState<AuthStatusType>(AUTH_STATUS.UNAUTHENTICATED);
  const [authInit, setAuthInit] = useState(false);

  const initAuth = useCallback(async () => {
    try {
      await AuthConfig.init();
      if (AuthConfig.validateIdToken()) {
        setAuthStatus(AUTH_STATUS.AUTHENTICATED);
      } else {
        await AuthConfig.saveToken();
        setAuthStatus(AUTH_STATUS.AUTHENTICATED);
      }
    } catch (err) {
      console.error(err);
      setAuthStatus(AUTH_STATUS.UNAUTHENTICATED);
    } finally {
      setAuthInit(true);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    if (!authInit) {
      return;
    }

    if (authStatus === AUTH_STATUS.UNAUTHENTICATED) {
      getLocalStorage().setItem("redirectURL", getURL());
      Auth.federatedSignIn({ customProvider: "AmazonFederate" });
    } else if (authStatus === AUTH_STATUS.AUTHENTICATED) {
      const redirectURL = getLocalStorage().getItem("redirectURL");
      if (redirectURL) {
        getLocalStorage().removeItem("redirectURL");
        redirect(redirectURL);
      }
    }
  }, [authStatus, authInit]);

  if (authStatus !== AUTH_STATUS.AUTHENTICATED) {
    return <KatSpinner id="authSpinner" size="large" variant="default" />;
  }

  return <>{children}</>;
};

export default AuthWrapper;
